import axios from 'axios';

import { deleteMedia, type Media, postMedia, updateMedia } from './media';
import { Option } from '../types/option';

const BASE_URL = '/api/categories';
const PUBLICATIONS_URL = `${BASE_URL}/publications`;

export type CategoryId = number;
export type CategoryPublicationId = number;

export enum CategoryStatus {
    ARCHIVE = 0,
    ACTIVE = 1,
}

export enum CategoryPublicationStatus {
    DRAFT = 0,
    ONLINE = 1,
}

export type Category = {
    id: CategoryId;
    title: string;
    priority: number;
    data: Media;
    status: number;
    createdAt: string;
    media: Option<Media>;
};

type CategoryPayload = {
    title: string;
    status: CategoryStatus;
};

type ReOrderCategoryPayload = {
    id: CategoryId;
    priority: number;
};

export type PaginatedCategoryPublication = {
    total: number;
    publications: CategoryPublication[];
};

export type CategoryPublication = {
    id: CategoryPublicationId;
    title: string;
    content: string;
    link: string;
    status: CategoryPublicationStatus;
    medias: Media[];
};

type CategoryPublicationPayload = {
    title: string;
    content: string;
    link: string;
    status: CategoryPublicationStatus;
};

export async function postCategoryMedia(categoryId: CategoryId, file: File): Promise<void> {
    await postMedia(`${BASE_URL}/${categoryId}/medias`, file, 0);
}

export async function postCategoryPublicationMedia(
    publicationId: CategoryPublicationId,
    file: File,
    priority: number,
): Promise<void> {
    await postMedia(`${PUBLICATIONS_URL}/${publicationId}/medias`, file, priority);
}

export type CategoryPublicationMediaSyncItem = {
    id: Option<number>;
    file: Option<File>;
    uploadSequence: number;
};

export async function syncCategoryPublicationMedias(
    publicationId: CategoryPublicationId,
    items: CategoryPublicationMediaSyncItem[],
    mediaIdsToDelete: ReadonlyArray<number> = [],
): Promise<void> {
    const sorted = [...items].sort((a, b) => a.uploadSequence - b.uploadSequence);
    const uploadPromises = sorted.map(item =>
        item.file
            .map(file =>
                item.id.match(
                    () => postCategoryPublicationMedia(publicationId, file, item.uploadSequence),
                    id => updateMedia(id, file, item.uploadSequence),
                ),
            )
            .getOrElse(Promise.resolve()),
    );

    await Promise.all([
        ...uploadPromises,
        ...mediaIdsToDelete.map(mediaId => deleteMedia(mediaId)),
    ]);
}

export async function getCategories(
    search: Option<string> = Option.none(),
    status: Option<CategoryStatus> = Option.none(),
): Promise<Category[]> {
    const { data } = await axios.get<(Omit<Category, 'media'> & { media: Media | null })[]>(
        BASE_URL,
        {
            params: {
                search: search.toNull(),
                status: status.toNull(),
            },
        },
    );

    return data.map(c => ({ ...c, media: Option.some(c.media) }));
}

export async function getCategoriesPublications(
    categoryId: CategoryId,
    page: number,
): Promise<PaginatedCategoryPublication> {
    const { data } = await axios.get<PaginatedCategoryPublication>(
        `${BASE_URL}/${categoryId}/publications`,
        {
            params: { page },
        },
    );
    return data;
}

export async function createCategoryPublication(
    categoryId: CategoryId,
    payload: CategoryPublicationPayload,
): Promise<CategoryPublication> {
    const { data } = await axios.post<CategoryPublication>(
        `${BASE_URL}/${categoryId}/publications`,
        payload,
    );
    return data;
}

export async function updateCategoryPublication(
    publicationId: CategoryPublicationId,
    payload: CategoryPublicationPayload,
): Promise<void> {
    await axios.put(`${PUBLICATIONS_URL}/${publicationId}`, payload);
}

export async function deleteCategoryPublication(
    publicationId: CategoryPublicationId,
): Promise<void> {
    await axios.delete(`${PUBLICATIONS_URL}/${publicationId}`);
}

export async function createCategory(payload: CategoryPayload): Promise<Category> {
    const { data } = await axios.post<Omit<Category, 'media'> & { media: Media | null }>(
        BASE_URL,
        payload,
    );
    return {
        ...data,
        media: Option.some(data.media),
    };
}

export async function updateCategory(
    categoryId: CategoryId,
    payload: CategoryPayload,
): Promise<void> {
    await axios.put(`${BASE_URL}/${categoryId}`, payload);
}

export async function reOrderCategory(payload: ReOrderCategoryPayload[]): Promise<void> {
    await axios.post(`${BASE_URL}:order`, payload);
}

export async function trackCategoryPublicationVisited(
    publicationId: CategoryPublicationId,
): Promise<void> {
    await axios.post(`${PUBLICATIONS_URL}/${publicationId}:visited`);
}

export async function trackCategoryPublicationCreated(
    publicationId: CategoryPublicationId,
): Promise<void> {
    await axios.post(`${PUBLICATIONS_URL}/${publicationId}:created`);
}
